import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tag } from 'lucide-react';

interface PriceBand {
  id: string;
  minDays: number;
  maxDays: number | null;
  label: string;
  price: number | null;
  unit: 'PER_DAY' | 'PER_BOOKING';
}

interface PriceBandDiscountRule {
  id: string;
  minDays: number;
  maxDays: number | null;
  percentOff: number;
  stackable: boolean;
  priority: number;
  activeFrom: Date | null;
  activeTo: Date | null;
  label: string | null;
}

interface PriceBandScheme {
  id: string;
  name: string;
  bands: PriceBand[];
  discounts: PriceBandDiscountRule[];
}

interface EquipmentPricingProps {
  priceBandScheme?: PriceBandScheme | null;
  isLoading?: boolean;
}

export function EquipmentPricing({ priceBandScheme, isLoading = false }: EquipmentPricingProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-32" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </div>
    );
  }

  // Check if we have price bands
  const hasPriceBands = priceBandScheme?.bands && priceBandScheme.bands.length > 0;
  
  if (!hasPriceBands) {
    return null; // Don't show anything if no price bands
  }

  // Sort price bands by minDays
  const sortedBands = [...priceBandScheme.bands].sort((a, b) => a.minDays - b.minDays);

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };


  return null; // Hidden for now
  
  // Hidden pricing display - keeping for future use
  // To restore: uncomment the return statement below and remove the "return null" above
  /*
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Tag className="h-5 w-5 text-[var(--color-rr-green)]" />
          Rental Pricing
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedBands.map((band, index) => (
            <Card key={band.id} className={`border-2 ${index === 0 ? 'border-[var(--color-rr-green)] bg-green-50' : 'border-gray-200'}`}>
              <CardContent className="p-4">
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-600 mb-2">
                    {band.label || formatDaysRange(band.minDays, band.maxDays)}
                  </div>
                  
                  <div className="mb-2">
                    {band.price ? (
                      <>
                        <div className="text-2xl font-bold text-[var(--color-rr-green)]">
                          {formatCurrency(band.price)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {band.unit === 'PER_DAY' ? 'per day' : 'per booking'}
                        </div>
                      </>
                    ) : (
                      <div className="text-2xl font-bold text-gray-600">POA</div>
                    )}
                  </div>
                  
                  {index === 0 && (
                    <Badge variant="default" className="bg-[var(--color-rr-green)] hover:bg-green-700">
                      Most Popular
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
  */
}